import React, { useState } from 'react';
import { ArrowLeft, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { Lead } from '../../../types';
import { LeadPromptSegment } from '../../../utils/tableUtils';
import { normalizeSourceName, parseSourceUrl, getFaviconUrl } from '../../../utils/leadPanelUtils';
import { isLinkedIn, isNotEmpty } from '../../../utils/validation';
import { LinkedInIcon } from '../../common/LinkedInIcon';

interface ReasoningCriterion {
  criterion_text: string;
  reasoning_text: string;
  satisfied: 'yes' | 'no' | 'unclear';
  source_urls: (string | any)[];
}

interface MobileDatasetDetailViewProps {
  lead: Lead;
  segments: LeadPromptSegment[];
  onBack: () => void;
}

export const MobileDatasetDetailView: React.FC<MobileDatasetDetailViewProps> = ({
  lead,
  // segments available via props but not used in current implementation
  onBack,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const data = lead.data || {};
  const reasoning = lead.reasoning || {};

  const primaryName = data.Name || data.name || Object.values(data)[0] || 'Unknown';
  const url = data.URL || data.url || data.Website || data.website || '';
  const description = data.Description || data.description || '';

  const allCriteria = Object.entries(reasoning).map(([key, value]) => ({
    key,
    criterion: value as ReasoningCriterion,
    value: data[key] || '',
  }));

  const matchedCount = allCriteria.filter(c => c.criterion.satisfied === 'yes').length;
  const totalCriteria = allCriteria.length;

  const toggleSection = (key: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedSections(newExpanded);
  };

  const displayUrl = typeof url === 'string'
    ? url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]
    : '';

  // Get additional fields (non-criteria, non-standard)
  const additionalFields = Object.entries(data).filter(([key, value]) => {
    const isStandardField = ['Name', 'name', 'URL', 'url', 'Website', 'website', 'Description', 'description'].includes(key);
    const isReasoningField = key in reasoning;
    return !isStandardField && !isReasoningField && isNotEmpty(value);
  });

  return (
    <div className="fixed inset-x-0 top-14 bottom-0 bg-[#F5F5F7] z-30 flex flex-col" data-tour="mobile-lead-detail">
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Combined Header - Back, Name, Match summary, LinkedIn icon */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200/50 px-4 py-3">
        {/* Top row: Back, Name, LinkedIn icon */}
        <div className="flex items-center gap-3">
          <button onClick={onBack} data-tour="mobile-back-button" className="p-2 -ml-2 rounded-xl active:bg-gray-100">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-[17px] font-semibold text-gray-900 truncate">
              {primaryName}
            </h1>
            {/* Match summary inline with URL */}
            <div className="flex items-center gap-2 mt-0.5">
              {displayUrl && (
                <p className="text-[12px] text-gray-500 truncate">
                  {displayUrl}
                </p>
              )}
              {displayUrl && totalCriteria > 0 && (
                <span className="text-gray-300">·</span>
              )}
              {totalCriteria > 0 && (
                <div className="flex items-center gap-1.5">
                  <div className="flex gap-0.5">
                    {allCriteria.slice(0, 6).map((item, idx) => (
                      <div
                        key={idx}
                        className={`w-1 h-3 rounded-full ${
                          item.criterion.satisfied === 'yes' ? 'bg-emerald-500' : 'bg-gray-200'
                        }`}
                      />
                    ))}
                    {totalCriteria > 6 && (
                      <span className="text-[10px] text-gray-400">+{totalCriteria - 6}</span>
                    )}
                  </div>
                  <span className="text-[12px] text-gray-500">
                    <span className="font-medium text-gray-700">{matchedCount}</span>/{totalCriteria}
                  </span>
                </div>
              )}
            </div>
          </div>
          {/* LinkedIn/Website icon on the right */}
          {url && (
            <a
              href={typeof url === 'string' && url.startsWith('http') ? url : `https://${url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-xl active:bg-gray-100 flex-shrink-0"
            >
              {typeof url === 'string' && url.toLowerCase().includes('linkedin') ? (
                <LinkedInIcon size={22} />
              ) : (
                <img
                  src={getFaviconUrl(displayUrl, 32)}
                  alt=""
                  className="w-[22px] h-[22px] rounded"
                  onError={(e: any) => {
                    e.target.onerror = null;
                    e.target.src = '';
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </a>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto no-scrollbar">
        <div className="p-4 space-y-4 pb-28">

          {/* Description */}
          {description && (
            <div className="bg-white rounded-2xl p-4">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                About
              </p>
              <p className="text-[14px] text-gray-700 leading-relaxed">
                {description}
              </p>
            </div>
          )}

          {/* Criteria Details */}
          {allCriteria.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide px-1">
                Criteria Details
              </p>
              {allCriteria.map((item, idx) => {
                const isMatch = item.criterion.satisfied === 'yes';
                const isExpanded = expandedSections.has(`criteria-${idx}`);
                const sources = (item.criterion.source_urls || [])
                  .map(parseSourceUrl)
                  .filter(Boolean)
                  .slice(0, 4);
                const isFirstCriterion = idx === 0;

                return (
                  <div
                    key={item.key}
                    className="bg-white rounded-2xl overflow-hidden"
                    {...(isFirstCriterion ? { 'data-tour': 'mobile-criteria-item' } : {})}
                  >
                    {/* Criteria header - always visible */}
                    <button
                      onClick={() => toggleSection(`criteria-${idx}`)}
                      className="w-full p-4 text-left active:bg-gray-50"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          isMatch ? 'bg-emerald-500' : 'bg-gray-200'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[14px] font-medium text-gray-900 leading-snug">
                            {item.criterion.criterion_text}
                          </p>
                          <p className={`text-[11px] font-medium mt-1 ${
                            isMatch ? 'text-gray-600' : 'text-gray-400'
                          }`}>
                            {isMatch ? 'Match' : 'No match'}
                          </p>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </div>
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                        {/* Justification */}
                        {item.criterion.reasoning_text && (
                          <div className="pt-3">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                              Justification
                            </p>
                            <p className="text-[13px] text-gray-600 leading-relaxed">
                              {item.criterion.reasoning_text}
                            </p>
                          </div>
                        )}

                        {/* Sources */}
                        {sources.length > 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-100">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                              Sources
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {sources.map((source: any, sIdx: number) => {
                                const isLI = isLinkedIn(source.hostname);

                                return (
                                  <a
                                    key={sIdx}
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl transition-colors ${
                                      isLI
                                        ? 'bg-[#0A66C2]/10 active:bg-[#0A66C2]/20'
                                        : 'bg-gray-100 active:bg-gray-200'
                                    }`}
                                  >
                                    {isLI ? (
                                      <LinkedInIcon size={16} />
                                    ) : (
                                      <img
                                        src={getFaviconUrl(source.hostname, 32)}
                                        alt=""
                                        className="w-4 h-4 rounded"
                                        onError={(e: any) => { e.target.style.display = 'none'; }}
                                      />
                                    )}
                                    <span className={`text-[12px] font-medium ${
                                      isLI ? 'text-[#0A66C2]' : 'text-gray-700'
                                    }`}>
                                      {isLI ? 'LinkedIn' : normalizeSourceName(source.hostname)}
                                    </span>
                                    <ExternalLink className={`w-3 h-3 ${
                                      isLI ? 'text-[#0A66C2]/60' : 'text-gray-400'
                                    }`} />
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Additional Information */}
          {additionalFields.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide px-1">
                Additional Info
              </p>
              <div className="bg-white rounded-2xl divide-y divide-gray-100">
                {additionalFields.slice(0, 8).map(([key, value]) => (
                  <div key={key} className="p-4">
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      {key.replace(/_/g, ' ')}
                    </p>
                    <p className="text-[14px] text-gray-900">
                      {typeof value === 'string' && value.length > 200
                        ? value.slice(0, 200) + '...'
                        : String(value)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
